﻿import { useQuery } from "@tanstack/react-query";
import { MinusCircle, Printer, RefreshCcw, Send, X as XIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { OrderEnvelope, OrderPayment } from "@tilt/pos-sdk";

import { getPosClient, sendPaymentReceipt } from "../../lib/api";
import { lookupMethodByTypeSubtype, resolveMethodIcon } from "../../lib/payment-methods";
import { readAccessToken } from "../../lib/auth";
import { Receipt } from "./Receipt";
import { formatCents } from "../../lib/format";
import { printReceiptUrl } from "../../lib/print-receipt";
import { useFocusTrap } from "../../lib/use-focus-trap";
import { cn } from "../../lib/utils";
import { Button } from "../ui/Button";
import { PaymentStatusBadge } from "../ui/StatusBadge";

export interface OrderReceiptsModalProps {
  readonly envelope: OrderEnvelope;
  readonly brand: Record<string, unknown> | null | undefined;
  readonly onClose: () => void;
}

/** Per-payment receipt actions modal.
 *
 * Layout: the receipt preview is always visible so the cashier can
 * read it before deciding to print or send via SMS. Payment rows appear
 * below the preview with per-row Print + Send SMS actions.
 *
 * Print buttons are hidden when receipt_print_enabled is false on the
 * order envelope (cascaded from partner → org → location config). */
export function OrderReceiptsModal({ envelope, brand, onClose }: OrderReceiptsModalProps) {
  const { t } = useTranslation();
  const dialogRef = useFocusTrap<HTMLDivElement>(true, onClose);
  const [showCancelled, setShowCancelled] = useState(false);
  const [receiptVariant, setReceiptVariant] = useState<"customer" | "merchant">("customer");
  const [smsState, setSmsState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const smsClearRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (smsClearRef.current != null) window.clearTimeout(smsClearRef.current);
    };
  }, []);

  async function handlePrint() {
    const token = readAccessToken();
    const url = getPosClient().receiptHtmlUrl(orderId, "order_consolidated", "order");
    await printReceiptUrl(url, token, t("receipt.printTitle"));
  }

  async function handleSendOrderSms() {
    setSmsState("sending");
    try {
      const result = await sendPaymentReceipt(envelope.order.id, { kind: "charge" });
      setSmsState(result.sent ? "sent" : "error");
    } catch {
      setSmsState("error");
    } finally {
      smsClearRef.current = window.setTimeout(() => setSmsState("idle"), 3000);
    }
  }

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const orderId = envelope.order.id;
  const currency = envelope.order.currency;
  const printEnabled = envelope.receipt_print_enabled;

  const cancelledCount = useMemo(
    () => envelope.payments.filter((p) => !!p.cancelled_at).length,
    [envelope.payments],
  );
  const visiblePayments = useMemo(
    () => (showCancelled ? envelope.payments : envelope.payments.filter((p) => !p.cancelled_at)),
    [envelope.payments, showCancelled],
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto py-4">
      <button
        type="button"
        aria-label={t("common.cancel")}
        onClick={onClose}
        className="fixed inset-0 cursor-default bg-black/40 backdrop-blur-sm"
        tabIndex={-1}
      />
      <div className="relative flex min-h-full items-center justify-center px-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="order-receipts-title"
          className="relative my-4 flex w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-surface shadow-elev-3 max-h-[calc(100vh-2rem)]"
        >
          <header className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-5 py-4">
            <h2 id="order-receipts-title" className="text-base font-semibold text-strong">
              {t("receipt.title")}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.cancel")}
              className="rounded-md p-1.5 text-muted transition hover:bg-surface-muted hover:text-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <XIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </header>

          {/* Body - only this region scrolls */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {/* Receipt preview */}
            <div className="border-b border-line bg-surface-muted">
              {/* Customer / Merchant copy toggle */}
              <div className="flex items-center gap-1 px-5 pt-3 pb-0">
                <button
                  type="button"
                  onClick={() => setReceiptVariant("customer")}
                  className={cn(
                    "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                    receiptVariant === "customer"
                      ? "bg-surface shadow-elev-1 text-strong"
                      : "text-muted hover:text-foreground",
                  )}
                >
                  {t("receipt.customerCopy", "Customer copy")}
                </button>
                <button
                  type="button"
                  onClick={() => setReceiptVariant("merchant")}
                  className={cn(
                    "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                    receiptVariant === "merchant"
                      ? "bg-surface shadow-elev-1 text-strong"
                      : "text-muted hover:text-foreground",
                  )}
                >
                  {t("receipt.merchantCopy", "Merchant copy")}
                </button>
              </div>
              <div className="px-5 py-4" aria-label={t("receipt.previewLabel", "Receipt preview")}>
                <Receipt envelope={envelope} brand={brand} variant={receiptVariant} />
              </div>
            </div>

            {/* Per-payment action rows */}
            <div className="divide-y divide-line px-5 py-2">
              {visiblePayments.map((payment) => (
                <PaymentReceiptRow
                  key={payment.link_id}
                  payment={payment}
                  currency={currency}
                  orderId={orderId}
                  locationId={envelope.order.location_id}
                  printEnabled={printEnabled}
                />
              ))}
              {visiblePayments.length === 0 && (
                <p className="py-6 text-center text-sm text-muted">
                  No payments on this order yet.
                </p>
              )}
              {cancelledCount > 0 && (
                <div className="py-3">
                  <button
                    type="button"
                    onClick={() => setShowCancelled((v) => !v)}
                    className="text-xs text-muted underline-offset-2 hover:underline"
                  >
                    {showCancelled
                      ? t("sale.timelineHideCancelled")
                      : t("sale.timelineShowCancelled", { count: cancelledCount })}
                  </button>
                </div>
              )}
            </div>
          </div>

          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-line px-5 py-4">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Send className="h-3.5 w-3.5" />}
              onClick={handleSendOrderSms}
              disabled={smsState === "sending"}
            >
              {smsState === "sending"
                ? "Sending…"
                : smsState === "sent"
                  ? "Sent!"
                  : smsState === "error"
                    ? "Failed"
                    : "Send SMS"}
            </Button>
            {printEnabled && (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Printer className="h-3.5 w-3.5" />}
                onClick={handlePrint}
              >
                Print receipt
              </Button>
            )}
          </footer>
        </div>
      </div>
    </div>
  );
}

function PaymentReceiptRow({
  payment,
  currency,
  orderId,
  locationId,
  printEnabled,
}: {
  readonly payment: OrderPayment;
  readonly currency: string;
  readonly orderId: string;
  readonly locationId?: string | null | undefined;
  readonly printEnabled: boolean;
}) {
  const railsQuery = useQuery({
    enabled: locationId != null,
    queryKey: ["payment-rails", locationId],
    queryFn: () => getPosClient().getLocationPaymentRails(locationId!),
    staleTime: 5 * 60_000,
  });
  const rails = useMemo(() => railsQuery.data?.rails ?? [], [railsQuery.data]);
  const rail = lookupMethodByTypeSubtype(
    rails,
    payment.payment_type,
    payment.payment_subtype ?? null,
  );

  const [printingProvider, setPrintingProvider] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{
    tone: "success" | "info" | "error";
    message: string;
  } | null>(null);
  const clearTimerRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (clearTimerRef.current != null) window.clearTimeout(clearTimerRef.current);
    };
  }, []);

  const kind: "charge" | "refund" = payment.role === "refund" ? "refund" : "charge";
  const targetId = kind === "refund" ? payment.payment_id : orderId;

  async function handlePrintPayment() {
    setPrintingProvider(true);
    try {
      const token = readAccessToken();
      // Always use the server-side per_payment receipt; it returns the
      // Valor HTML auth slip if one was stored, or the tilt template
      // (clean auth-slip layout, no order totals) as fallback.
      const receiptKind = kind === "refund" ? "refund" : "per_payment";
      const url = getPosClient().receiptHtmlUrl(orderId, receiptKind, payment.payment_id);
      await printReceiptUrl(url, token, "Payment receipt");
    } finally {
      setPrintingProvider(false);
    }
  }

  async function onSend() {
    setSubmitting(true);
    setFeedback(null);
    if (clearTimerRef.current != null) {
      window.clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
    try {
      const result = await sendPaymentReceipt(orderId, {
        kind,
        target_id: targetId,
        ...(kind === "charge" ? { payment_id: payment.payment_id } : {}),
      });
      if (result.sent) {
        setFeedback({ tone: "success", message: "Receipt sent" });
      } else if (result.skipped_reason === "vendor_error") {
        setFeedback({ tone: "error", message: "Carrier rejected. Try again later." });
      } else {
        setFeedback({ tone: "info", message: humaniseSkip(result.skipped_reason) });
      }
    } catch (err) {
      setFeedback({ tone: "error", message: err instanceof Error ? err.message : "Send failed" });
    } finally {
      setSubmitting(false);
      clearTimerRef.current = window.setTimeout(() => {
        setFeedback(null);
        clearTimerRef.current = null;
      }, 3000);
    }
  }

  return (
    <div className="py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-muted text-muted">
          {payment.role === "refund" ? (
            <RefreshCcw className="h-3.5 w-3.5" />
          ) : (
            (() => {
              const Icon = rail ? resolveMethodIcon(rail) : MinusCircle;
              return <Icon className="h-3.5 w-3.5" aria-hidden />;
            })()
          )}
        </span>
        <span
          className={cn(
            "text-sm font-semibold tabular-nums",
            payment.role === "refund" ? "text-red-600" : "text-strong",
          )}
        >
          {payment.role === "refund" ? "−" : ""}
          {formatCents(payment.applied_amount_cents, currency)}
        </span>
        <PaymentStatusBadge status={payment.status} size="sm" />
        <div className="ml-auto flex flex-wrap gap-2">
          {printEnabled && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={
                printingProvider ? (
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <Printer className="h-3.5 w-3.5" />
                )
              }
              onClick={handlePrintPayment}
              disabled={printingProvider}
            >
              Print
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Send className="h-3.5 w-3.5" />}
            onClick={onSend}
            disabled={submitting}
          >
            {submitting ? "Sending…" : "Send SMS"}
          </Button>
        </div>
      </div>
      {feedback && (
        <p
          className={cn(
            "mt-1 text-[11px]",
            feedback.tone === "success" && "text-emerald-600",
            feedback.tone === "info" && "text-muted",
            feedback.tone === "error" && "text-red-600",
          )}
        >
          {feedback.message}
        </p>
      )}
    </div>
  );
}

function humaniseSkip(reason: string): string {
  switch (reason) {
    case "no_recipient":
      return "No customer phone on file.";
    case "no_binding":
      return "SMS not configured for this location.";
    case "no_credential":
      return "Twilio credentials missing - ask an admin.";
    case "not_found":
      return "Order or payment not found.";
    case "":
      return "Sent (no carrier confirmation yet).";
    default:
      return `Skipped: ${reason}`;
  }
}
